import React, { useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import { GSD_SOURCE_KEY } from '@intelligence/_config/sourceMetric';
import {
  getDataSourcesDisabledStateNew,
  getMetricDisabledNew,
} from '@intelligence/utils/commonUtils';
import { MetricsTableTopChartsTypes } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import InfoDoc from '@intelligence/_locales/en-US/infoDoc';
import InfoDocCN from '@intelligence/_locales/zh-CN/infoDoc';
import { toDateTypeNor } from '@intelligence/utils/common';
import type { IGameType } from '@intelligence/types/common';
import type { getDataSourcesDisabledStateParams } from '@intelligence/utils/paramsInterface';
import type { PCConsolePageId } from '@intelligence/pages/TopCharts/PCGames/_config/type';

// 数据源由于筛选条件导致禁用时 的 提示文案
const FILTER_CONDITIONAL_DISABLED_TIPS: {
  [key: string]: {
    message: string;
    messageCN: string;
  };
} = {
  Mscience: {
    message: InfoDoc.dbi_info_mscience_available_only,
    messageCN: InfoDocCN.dbi_info_mscience_available_only,
  },
  [GSD_SOURCE_KEY]: {
    message: InfoDoc.dbi_info_gsd_metrics_category_disabled_tips,
    messageCN: InfoDocCN.dbi_info_gsd_metrics_category_disabled_tips,
  },
  npd: {
    message: InfoDoc.dbi_info_physical_monthly_only,
    messageCN: InfoDocCN.dbi_info_physical_monthly_only,
  },
  newzoo: {
    message: InfoDoc.dbi_info_newzoo_metrics_category_disabled_tips,
    messageCN: InfoDocCN.dbi_info_newzoo_metrics_category_disabled_tips,
  },
  steam: {
    message: InfoDoc.dbi_info_steam_metrics,
    messageCN: InfoDocCN.dbi_info_steam_metrics,
  },
  vginsights: {
    message: InfoDoc.dbi_info_vgi_metrics_category_disabled_tips,
    messageCN: InfoDocCN.dbi_info_vgi_metrics_category_disabled_tips,
  },
  gamalytic: {
    message: InfoDoc.dbi_info_steam_data_metrics_category_disabled_tips,
    messageCN: InfoDocCN.dbi_info_steam_data_metrics_category_disabled_tips,
  },
  alinea: {
    message: InfoDoc.dbi_info_steam_data_metrics_category_disabled_tips,
    messageCN: InfoDocCN.dbi_info_steam_data_metrics_category_disabled_tips,
  },
  // Famitsu is temporarily unavailable in Intelligence pages.
  // famitsu: {
  //   message: InfoDoc.dbi_info_console_japan_sales_metrics_category_disabled_tips,
  //   messageCN: InfoDocCN.dbi_info_console_japan_sales_metrics_category_disabled_tips,
  // },
  ampere: {
    message: InfoDoc.dbi_info_other_vendor_disabled_tips,
    messageCN: InfoDocCN.dbi_info_other_vendor_disabled_tips,
  },
};

/**
 * 获取 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表
 * @param entityType 实体类型
 * @param isPCConsoleCombine 实体类型是否为combine
 * @returns 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表
 */
const getWillUpdateDataSourceMetricsOptions = (
  entityType: IGameType,
  isPCConsoleCombine: boolean,
): any[] => {
  // 需要更新 是否禁用的 数据源options配置，index保存 在dataSourceMetricsOptions中的索引，value保存 数据源的value，disabled 保存该数据源 新的禁用状态
  const options = [
    {
      index: -1,
      value: 'Mscience',
      disabled: false,
    },
    {
      index: -1,
      value: GSD_SOURCE_KEY,
      disabled: false,
    },
    {
      index: -1,
      value: 'npd',
      disabled: false,
    },
    {
      index: -1,
      value: 'newzoo',
      disabled: false,
    },
  ];

  if (entityType === 'pc') {
    options.unshift(
      {
        index: -1,
        value: 'steam',
        disabled: false,
      },
      {
        index: -1,
        value: 'SteamSales',
        disabled: false,
      },
    );
  }

  if (isPCConsoleCombine) {
    options.unshift(
      {
        index: -1,
        value: 'steam',
        disabled: false,
      },
      {
        index: -1,
        value: 'alinea',
        disabled: false,
      },
      {
        index: -1,
        value: 'gamalytic',
        disabled: false,
      },
      {
        index: -1,
        value: 'vginsights',
        disabled: false,
      },
      {
        index: -1,
        value: 'ampere',
        disabled: false,
      },
      // Famitsu is temporarily unavailable in Intelligence pages.
      // {
      //   index: -1,
      //   value: 'famitsu',
      //   disabled: false,
      // },
    );
  }

  return options;
};

/**
 * 更新 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表 每一个的禁用状态、禁用提示文案
 * @param options 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表
 * @param disabledState 数据源禁用状态
 */
const updateDisabledInfoOfWillUpdateDataSourceMetricsOptions = (payload: {
  options: any[];
  disabledState: { [key: string]: boolean };
  columns: any[];
  isPCConsoleCombine: boolean;
}) => {
  const { options, disabledState, columns, isPCConsoleCombine } = payload || {};
  if (!Array.isArray(options)) return;
  for (const option of options) {
    const optionValue = option?.value;
    if (!optionValue) continue;
    if (optionValue === 'SteamSales') {
      option.disabled = false;
      option.message = '';
      option.messageCN = '';
      continue;
    }
    // 是否为combine，并且宽表数据源指标没有勾选当前数据源指标
    const isPCConsoleCombineNotChecked =
      isPCConsoleCombine &&
      optionValue !== 'steam' &&
      !columns?.some((item: any) => {
        return item?.dataSource?.toLowerCase() === optionValue?.toLowerCase();
      });
    // 是否因为筛选条件导致禁用
    const isFilterConditionalDisabled = disabledState?.[optionValue] || false;
    // 因为筛选条件导致禁用时的提示文案
    const filterConditionalDisabledTip = FILTER_CONDITIONAL_DISABLED_TIPS[optionValue];
    // 英文提示文案
    const disabledMessage =
      isPCConsoleCombineNotChecked && !isFilterConditionalDisabled
        ? InfoDoc.dbi_info_metric_not_checked_disabled_tips
        : filterConditionalDisabledTip?.message;
    // 中文提示文案
    const disabledMessageCN =
      isPCConsoleCombineNotChecked && !isFilterConditionalDisabled
        ? InfoDocCN.dbi_info_metric_not_checked_disabled_tips
        : filterConditionalDisabledTip?.messageCN;

    option.disabled = isFilterConditionalDisabled || isPCConsoleCombineNotChecked || false;
    option.message = disabledMessage || '';
    option.messageCN = disabledMessageCN || '';
  }
};

/**
 * 更新 willUpdateDataSourceMetricsOptions 中 所有的index索引
 */
const updateIndexOfWillUpdateDataSourceMetricsOptions = (
  options: any[],
  willUpdateOptions: any[],
) => {
  for (let i = 0; i < options?.length; i++) {
    const option = options?.[i];
    for (let j = 0; j < willUpdateOptions.length; j++) {
      const willUpdateOption = willUpdateOptions[j];
      if (willUpdateOption.value === option?.value) {
        willUpdateOption.index = i;
        break;
      }
    }
    if (willUpdateOptions.every((item) => item.index !== -1)) break;
  }
};

export default function useMetricsOptionsDisabledEffect(props: {
  entityType: IGameType;
  pageId: PCConsolePageId;
  topChartsType: string;
  isPCConsoleCombine: boolean;
  fetchParams: getDataSourcesDisabledStateParams;
  dataSourcesColumns: any[];
  dataSourceMetricsOptions: any[];
  setDataSourceMetricsOptions: React.Dispatch<React.SetStateAction<any[]>>;
}) {
  const {
    entityType,
    pageId,
    topChartsType,
    isPCConsoleCombine,
    fetchParams,
    dataSourcesColumns,
    dataSourceMetricsOptions,
    setDataSourceMetricsOptions,
  } = props || {};

  // 判断此可视化指标数据源，所有指标是否全部为禁选
  const getIsAllDisabled = (metricOption: any) => {
    const childList = Array.isArray(metricOption?.children) ? metricOption?.children : [];
    const hasDisabledFalse = childList?.every((item: any) => item.disabled === true);
    return hasDisabledFalse;
  };

  /**
   * 处理MetricsTable类型榜单页，包含pc、console、combine
   */
  const handleWhenMetricsTablePage = useMemoizedFn(() => {
    if (!MetricsTableTopChartsTypes.includes(topChartsType)) return;
    let dataSourceMetricsOptionsTmp: any[] = [...dataSourceMetricsOptions];
    // 获取数据源的禁用状态
    const dataSourcesDisabledState = getDataSourcesDisabledStateNew({
      pageId,
      params: fetchParams,
    });
    // console.log('dataSourceMetricsOptionsTmp', dataSourceMetricsOptionsTmp);
    // console.log('dataSourcesDisabledState', dataSourcesDisabledState);
    // 获取 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表
    const willUpdateDataSourceMetricsOptions = getWillUpdateDataSourceMetricsOptions(
      entityType,
      isPCConsoleCombine,
    );

    // console.log('willUpdateDataSourceMetricsOptions', willUpdateDataSourceMetricsOptions);

    // 更新 待更新禁用状态、禁用提示文案的 dataSourceMetricsOptions列表 每一个的禁用状态、禁用提示文案
    updateDisabledInfoOfWillUpdateDataSourceMetricsOptions({
      options: willUpdateDataSourceMetricsOptions,
      disabledState: dataSourcesDisabledState,
      columns: dataSourcesColumns,
      isPCConsoleCombine,
    });

    // 更新 willUpdateDataSourceMetricsOptions 中 所有的index索引
    updateIndexOfWillUpdateDataSourceMetricsOptions(
      dataSourceMetricsOptionsTmp,
      willUpdateDataSourceMetricsOptions,
    );

    // 用willUpdateDataSourceMetricsOptions中保存的 数据源索引、数据源最新禁用状态  更新 dataSourceMetricsOptionsTmp中的 对应数据源状态
    for (const willUpdateDisabledMetricOption of willUpdateDataSourceMetricsOptions) {
      const willUpdateDisabledMetricOptionIndex = willUpdateDisabledMetricOption.index;
      if (willUpdateDisabledMetricOptionIndex === -1) continue;
      let dataSourceMetricOptionTmp =
        dataSourceMetricsOptionsTmp[willUpdateDisabledMetricOptionIndex];
      if (!dataSourceMetricOptionTmp) continue;
      dataSourceMetricOptionTmp = {
        ...dataSourceMetricOptionTmp,
        disabled: willUpdateDisabledMetricOption.disabled,
        message: willUpdateDisabledMetricOption.message,
        messageCN: willUpdateDisabledMetricOption.messageCN,
      };

      // console.log('dataSourceMetricOptionTmp', dataSourceMetricOptionTmp);
      // console.log('willUpdateDisabledMetricOption', willUpdateDisabledMetricOption);

      const metricOptionChildrenTmp = dataSourceMetricOptionTmp?.children?.map((item: any) => {
        const isFilterConditionalDisabled = getMetricDisabledNew({
          pageId,
          metric: item?.value,
          isDataSourceDisabled: false,
          params: fetchParams,
        });
        // console.log('isFilterConditionalDisabled', isFilterConditionalDisabled);

        const isPCConsoleCombineNotChecked =
          isPCConsoleCombine &&
          !dataSourcesColumns?.some((subItem: any) => {
            return subItem?.value === item?.value;
          });
        // console.log('isPCConsoleCombineNotChecked', isPCConsoleCombineNotChecked);
        const disabledMessage =
          isPCConsoleCombineNotChecked && !isFilterConditionalDisabled
            ? InfoDoc?.dbi_info_metric_not_checked_disabled_tips
            : item?.message || item?.bubbleContent || '';
        const disabledMessageCN =
          isPCConsoleCombineNotChecked && !isFilterConditionalDisabled
            ? InfoDocCN?.dbi_info_metric_not_checked_disabled_tips
            : item?.messageCN || item?.bubbleContentCN || '';

        // 新添加时间粒度禁选指标逻辑
        const isPCConsoleCombineDisabledDateType =
          isPCConsoleCombine &&
          item?.ableDateType &&
          fetchParams?.date_type &&
          item?.ableDateType !== toDateTypeNor[fetchParams?.date_type];

        return {
          ...item,
          disabled:
            isFilterConditionalDisabled ||
            isPCConsoleCombineNotChecked ||
            isPCConsoleCombineDisabledDateType,
          message: disabledMessage || '',
          messageCN: disabledMessageCN || '',
        };
      });
      dataSourceMetricOptionTmp = {
        ...dataSourceMetricOptionTmp,
        children: metricOptionChildrenTmp || [],
      };

      dataSourceMetricsOptionsTmp = [
        ...dataSourceMetricsOptionsTmp.slice(0, willUpdateDisabledMetricOptionIndex),
        dataSourceMetricOptionTmp,
        ...dataSourceMetricsOptionsTmp.slice(willUpdateDisabledMetricOptionIndex + 1),
      ];
    }

    // 所有子指标判断过是否禁选后，在原有非禁选逻辑基础上，再判断是否全部禁选，来决定数据源是否可选
    const dataSourceMetricOptionList = [...dataSourceMetricsOptionsTmp];
    const dataSourceMetricOptionListCur = dataSourceMetricOptionList?.map((item: any) => {
      return {
        ...item,
        disabled: item.disabled === true ? item.disabled : getIsAllDisabled(item),
      };
    });
    setDataSourceMetricsOptions(dataSourceMetricOptionListCur);
  });

  /**
   * 处理DigitalRevenue类型榜单页，包含pc、console
   */
  const handleWhenDigitalRevenuePage = useMemoizedFn(() => {
    if (pageId !== 'DigitalRevenue') return;
    const { regions } = fetchParams || {};
    if (!(regions?.length === 1 && regions[0] === 'us')) return;
    const dataSourceMetricsOptionsTmp: any[] = dataSourceMetricsOptions?.map((item: any) => {
      const dataSourceDisabledTmp = item?.value === 'digitalARPPU' ? false : item?.disabled;
      return {
        ...item,
        disabled: dataSourceDisabledTmp,
      };
    });
    setDataSourceMetricsOptions(dataSourceMetricsOptionsTmp || []);
  });

  useEffect(() => {
    handleWhenMetricsTablePage();

    handleWhenDigitalRevenuePage();
  }, [
    fetchParams?.regions,
    fetchParams?.markets,
    fetchParams?.date_type,
    entityType,
    isPCConsoleCombine,
    dataSourcesColumns,
  ]);
}
